import { Link } from 'react-router-dom';
import type { DaySummary } from '@/shared/api/types';
import { formatKcal, formatTotalKcal, isRange } from '@/shared/format/nutrition';
import { Card, Eyebrow, MacroGrid, Progress } from '@/shared/ui';

/**
 * La tarjeta del total del día.
 *
 * El chip de excluidos va pegado al número y no al pie de la tarjeta: el aviso que corrige un dato
 * tiene que viajar con el dato, o se lee el total como si fuera completo y el aviso llega tarde.
 */
export function DayTotalCard({
  summary,
  onShowExcluded,
}: {
  summary: DaySummary;
  onShowExcluded: () => void;
}) {
  const { consumed, goal, remaining } = summary;
  const excludedCount = summary.excluded.length;
  const range = isRange(consumed);

  return (
    <Card size="lg">
      <div className="row-between">
        <Eyebrow>Total del día</Eyebrow>
        {excludedCount > 0 && (
          <button type="button" className="chip" onClick={onShowExcluded}>
            <span className="chip-dot" />
            {excludedCount} {excludedCount === 1 ? 'ítem fuera' : 'ítems fuera'} del total
          </button>
        )}
      </div>

      <div className="stack-tight stack">
        <div className={range ? 'figure figure-range' : 'figure'}>
          {range ? (
            <>
              <span className="figure-value">{formatKcal(consumed.kcalLow)}</span>
              <span className="figure-dash">–</span>
              <span className="figure-value">{formatKcal(consumed.kcalHigh)}</span>
            </>
          ) : (
            <span className="figure-value">{formatKcal(consumed.kcal)}</span>
          )}
          <span className="figure-unit">kcal</span>
        </div>

        {range && (
          <p className="micro muted">
            Es un rango porque hay ítems estimados de una foto. Los macros con ≈ vienen de la
            misma estimación. Confirmalos y el total se fija.
          </p>
        )}
      </div>

      {goal === null || remaining === null ? (
        /*
         * CU-09 A1 · sin objetivo desaparecen la barra y los restantes, y queda una invitación que
         * no insiste. Mostrar una barra vacía o ceros haría que "no tengo objetivo" y "me quedan
         * cero calorías" se vieran igual.
         */
        <div className="card-dashed">
          <span className="small">
            Todavía no fijaste un objetivo, así que esto es sólo lo que comiste.
          </span>
          <Link to="/objetivos" className="small">
            Definir uno
          </Link>
        </div>
      ) : (
        <>
          <Progress
            certain={consumed.kcalLow}
            uncertain={consumed.kcalHigh - consumed.kcalLow}
            max={goal.targetKcal}
            label="Avance del día"
          />
          <div className="row-between small" style={{ fontWeight: 600 }}>
            <span className="numeric">
              {remaining.kcalHigh >= 0
                ? `Te quedan ${formatTotalKcal(remaining)} de ${formatKcal(goal.targetKcal)} kcal`
                : `Llevás ${formatKcal(Math.abs(remaining.kcal))} kcal por encima de ${formatKcal(goal.targetKcal)}`}
            </span>
            <span className="numeric muted">
              {percentageLabel(consumed.kcalLow, consumed.kcalHigh, goal.targetKcal)}
            </span>
          </div>
        </>
      )}

      <MacroGrid
        proteinG={consumed.proteinG}
        carbohydratesG={consumed.carbohydratesG}
        fatG={consumed.fatG}
        target={goal?.macros}
        estimated={range}
      />
    </Card>
  );
}

function percentageLabel(low: number, high: number, target: number): string {
  if (target <= 0) {
    return '';
  }

  const lowPercentage = Math.round((low / target) * 100);
  const highPercentage = Math.round((high / target) * 100);

  return lowPercentage === highPercentage
    ? `${lowPercentage} %`
    : `${lowPercentage}–${highPercentage} %`;
}
